import React, { useState, useEffect } from 'react';
import {
  Upload,
  FlipHorizontal,
  FlipVertical,
  Maximize2,
  Sparkles,
  ChevronDown,
  CircleSlash,
  Cloud,
  ArrowRight,
  // ArrowUp,
  RotateCw,
  Palette,
  ChevronUp,
  Trash2,
  Loader2,
  Repeat,
  Lock,
  Unlock,
  Crop,
  Eraser,
} from 'lucide-react';
import { ImageCropModal } from '../../features/image-crop/ImageCropModal';
import { BackgroundEraserModal } from '../../features/background-eraser/BackgroundEraserModal';
import { motion, AnimatePresence } from 'motion/react';
import { createPortal } from 'react-dom';
import { useUploadAssetMutation } from '../../features/assets/assetsApi';
import { normalizeId } from '../../utils/assetUtils';
import { Database } from 'lucide-react';

const ANIMATION_OPTIONS = [
  { id: 'none', label: 'None', icon: CircleSlash, previewClass: '' },
  { id: 'fadeIn', label: 'Fade', icon: Cloud, previewClass: 'preview-animation-fade' },
  { id: 'slideIn', label: 'Slide', icon: ArrowRight, previewClass: 'preview-animation-slideIn' },
  { id: 'zoomIn', label: 'Zoom', icon: Maximize2, previewClass: 'preview-animation-rise' },
  { id: 'rotate', label: 'Rotate', icon: RotateCw, previewClass: 'preview-animation-rotate' },
  // { id: 'parallax', label: 'Parallax', icon: ArrowUp, previewClass: 'preview-animation-slideIn' },
];
import Opacity from "../../../imports/Opacity";
import Corner from "../../../imports/Corner";
import Border from "../../../imports/Border";

export interface ImageSettings {
  url: string;
  assetId?: string;
  opacity: number;
  brightness: number;
  contrast: number;
  saturation: number;
  blur: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
  borderWidth: number;
  borderColor: string;
  borderRadius: number;
  objectFit: 'cover' | 'contain' | 'fill' | 'none';
  lockAspectRatio: boolean;
  animation: import('../../types/design').AnimationSettings;
  crop?: {
    enabled: boolean;
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  binding?: {
    customApiId: string;
    apiKey: string;
    fallbackValue?: string;
  };
}

interface ImagePropertiesPanelProps {
  selectedImageId: string | null;
  settings: ImageSettings;
  dimensions?: { left: number; top: number; width: number; height: number; rotation?: number };
  onSettingsChange: (settings: Partial<ImageSettings>, commit?: boolean) => void;
  onDimensionsChange?: (dimensions: Partial<{ left: number; top: number; width: number; height: number; rotation?: number }>) => void;
  onDimensionsCommit?: () => void;
  onClose: () => void;
  onDelete?: (id: string) => void;
  onAutoMapAll?: (apiId: string, activeLayerBindingOverride?: any) => void;
  layerName?: string;
  onOpenDataBindingSidepane?: () => void;
}

const PropertyInput: React.FC<{
  label?: string;
  value: number;
  onChange: (val: number) => void;
  width?: string;
  unit?: string;
  step?: number;
  min?: number;
  onBlur?: () => void;
}> = ({ label, value, onChange, width = "w-9", unit, step = 1, min, onBlur }) => (
  <div className="flex items-center h-7 px-2 rounded-lg bg-gray-50 border border-transparent hover:border-gray-200 focus-within:border-[#30A84B] focus-within:bg-white focus-within:shadow-[0_2px_4px_rgba(0,0,0,0.02)] transition-all group relative">
    {label && (
      <span className="text-[9px] font-black text-gray-400 mr-1.5 select-none uppercase tracking-widest group-focus-within:text-[#30A84B]">
        {label}
      </span>
    )}
    <input
      type="number"
      value={typeof value === 'number' ? (step < 1 ? value : Math.round(value)) : 0}
      onChange={(e) => {
        const val = parseFloat(e.target.value);
        if (!isNaN(val)) onChange(val);
      }}
      onBlur={() => onBlur?.()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onBlur?.();
      }}
      className={`${width} bg-transparent text-[11px] font-bold text-gray-700 outline-none text-left [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none pl-0.5`}
      step={step}
      min={min}
    />
    {unit && <span className="text-[10px] text-gray-400 font-bold ml-0.5">{unit}</span>}
    <div className="flex flex-col ml-1 opacity-0 group-hover:opacity-100 transition-all duration-200">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onChange(value + step); }}
        className="hover:bg-gray-200 rounded-sm p-0.5 text-gray-400 hover:text-gray-900 transition-colors"
      >
        <ChevronUp className="w-2.5 h-2.5" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onChange(min !== undefined ? Math.max(min, value - step) : value - step); }}
        className="hover:bg-gray-200 rounded-sm p-0.5 text-gray-400 hover:text-gray-900 transition-colors"
      >
        <ChevronDown className="w-2.5 h-2.5" />
      </button>
    </div>
  </div>
);

export const ImagePropertiesPanel: React.FC<ImagePropertiesPanelProps> = ({
  selectedImageId,
  settings,
  dimensions,
  onSettingsChange,
  onDimensionsChange,
  onDimensionsCommit,
  onDelete,
  onOpenDataBindingSidepane,
}) => {
  const [localSettings, setLocalSettings] = useState(settings);
  const [uploadAsset, { isLoading: isUploading }] = useUploadAssetMutation();

  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [isEraserModalOpen, setIsEraserModalOpen] = useState(false);

  const [isAnimDropdownOpen, setIsAnimDropdownOpen] = useState(false);
  const [activePopover, setActivePopover] = useState<'border' | null>(null);
  const [animDropdownPos, setAnimDropdownPos] = useState({ top: 0, left: 0 });
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const [hoveredAnim, setHoveredAnim] = useState<string | null>(null);
  const animTriggerRef = React.useRef<HTMLButtonElement>(null);
  const colorTriggerRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  React.useLayoutEffect(() => {
    if (isAnimDropdownOpen && animTriggerRef.current) {
      const updatePosition = () => {
        if (animTriggerRef.current) {
          const rect = animTriggerRef.current.getBoundingClientRect();
          setAnimDropdownPos({
            top: rect.bottom + 8,
            left: Math.max(16, rect.left - 120)
          });
        }
      };
      updatePosition();
      window.addEventListener('resize', updatePosition);
      return () => window.removeEventListener('resize', updatePosition);
    }
  }, [isAnimDropdownOpen]);

  React.useLayoutEffect(() => {
    if (activePopover === 'border' && colorTriggerRef.current) {
      const rect = colorTriggerRef.current.getBoundingClientRect();
      setPopoverPos({
        top: rect.bottom + 8,
        left: Math.max(16, rect.left - 80)
      });
    }
  }, [activePopover]);

  useEffect(() => {
    if (!isAnimDropdownOpen && !activePopover) return;
    const handleClickOutside = (e: MouseEvent) => {
      const animPortal = document.getElementById('image-anim-portal');
      const colorPortal = document.getElementById('image-color-portal');
      const isAnimTrigger = animTriggerRef.current?.contains(e.target as Node);
      const isColorTrigger = colorTriggerRef.current?.contains(e.target as Node);

      if (animPortal && !animPortal.contains(e.target as Node) && !isAnimTrigger) {
        setIsAnimDropdownOpen(false);
      }
      if (colorPortal && !colorPortal.contains(e.target as Node) && !isColorTrigger) {
        setActivePopover(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAnimDropdownOpen, activePopover]);

  const updateSetting = (key: keyof ImageSettings, value: any, commit = false) => {
    const newSettings = { ...localSettings, [key]: value };
    console.log([key], value, 'ugkkkkkkkkkk');
    setLocalSettings(newSettings);
    onSettingsChange({ [key]: value }, commit);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', file.name);

        const response = await uploadAsset(formData).unwrap() as any;
        const imageUrl = response?.data?.url || response?.url;
        const assetId = normalizeId(response?.data?._id || response?._id);

        if (imageUrl) {
          updateSetting('assetId', assetId);
          updateSetting('url', imageUrl, true);
        }
      } catch (err) {
        console.error('Failed to upload image:', err);
      }
    }
  };

  const updateAnimationSetting = (key: string, value: any, commit = true) => {
    const currentAnim = localSettings.animation || {
      type: 'none',
      duration: 1000,
      delay: 0,
      easing: 'ease',
      loop: false
    };
    updateSetting('animation', {
      ...currentAnim,
      [key]: value
    }, commit);
  };

  if (!selectedImageId) return null;

  const updateDimension = (key: string, value: number) => {
    if (onDimensionsChange) {
      onDimensionsChange({ [key]: value });
    }
  };

  return (
    <div className="flex flex-col gap-1.5 p-0.5" onMouseDown={(e) => e.stopPropagation()}>
      {/* Row 1: Transform & Actions */}
      <div className="flex items-center h-8 px-1 gap-0.5">
        {/* Label */}
        <div className="px-2 text-[10px] font-black text-[#30A84B] uppercase tracking-widest border-r border-gray-100 mr-1 select-none whitespace-nowrap">
          Image
        </div>

        {/* Position Section */}
        {dimensions && (
          <div className="flex items-center gap-1.5 px-2 border-r border-gray-100 mr-1" title="Position">
            <div className="flex items-center gap-1">
              <PropertyInput
                label="X"
                value={dimensions.left}
                onChange={(val) => updateDimension('left', val)}
                onBlur={onDimensionsCommit}
              />
              <PropertyInput
                label="Y"
                value={dimensions.top}
                onChange={(val) => updateDimension('top', val)}
                onBlur={onDimensionsCommit}
              />
            </div>
          </div>
        )}

        {/* Size Section */}
        {dimensions && (
          <div className="flex items-center gap-1.5 px-2 border-r border-gray-100 mr-1" title="Size">
            <Maximize2 className="w-5 h-5 text-gray-400" />
            <div className="flex items-center gap-1">
              <PropertyInput
                label="W"
                value={dimensions.width}
                onChange={(val) => updateDimension('width', val)}
                onBlur={onDimensionsCommit}
                width="w-12"
              />
              <PropertyInput
                label="H"
                value={dimensions.height}
                onChange={(val) => updateDimension('height', val)}
                onBlur={onDimensionsCommit}
                width="w-12"
              />
              <button
                type="button"
                onClick={() => updateSetting('lockAspectRatio', !(localSettings.lockAspectRatio ?? true), true)}
                className={`p-1 rounded transition-all ${(localSettings.lockAspectRatio ?? true) ? 'text-[#30A84B] bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                title={(localSettings.lockAspectRatio ?? true) ? "Aspect ratio locked" : "Aspect ratio unlocked (free stretch/resize)"}
              >
                {(localSettings.lockAspectRatio ?? true) ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        )}

        {/* Rotation Section */}
        {dimensions && (
          <div className="flex items-center gap-1.5 px-2 border-r border-gray-100 mr-1" title="Rotation">
            <RotateCw className="w-5 h-5 text-gray-400" />
            <PropertyInput
              label=""
              value={dimensions.rotation || 0}
              onChange={(val) => updateDimension('rotation', val)}
              onBlur={onDimensionsCommit}
              unit="°"
              width="w-10"
            />
          </div>
        )}

        {/* Upload/Replace */}
        <div className="flex items-center h-7 px-1 border-r border-gray-100 mr-1">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
            id="image-toolbar-upload"
          />
          <label
            htmlFor={isUploading ? undefined : "image-toolbar-upload"}
            className={`flex items-center gap-1.5 h-6 px-2.5 rounded-md text-[13px] font-semibold text-gray-700 transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 cursor-pointer'
              }`}
          >
            {isUploading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Upload className="w-5 h-5" />
            )}
            <span>{isUploading ? 'Uploading...' : 'Replace'}</span>
          </label>
        </div>

        {/* Crop Feature (WEJHA-315) */}
        <button
          type="button"
          onClick={() => setIsCropModalOpen(true)}
          className="flex items-center gap-1.5 h-6 px-2.5 rounded-md text-[13px] font-semibold text-gray-700 hover:bg-gray-100 transition-colors border-r border-gray-100 mr-1"
          title="Crop Image"
        >
          <Crop className="w-4 h-4 text-[#30A84B]" />
          <span>Crop</span>
        </button>

        {/* Background Eraser (WEJHA-316) */}
        <button
          type="button"
          onClick={() => setIsEraserModalOpen(true)}
          className="flex items-center gap-1.5 h-6 px-2.5 rounded-md text-[13px] font-semibold text-gray-700 hover:bg-gray-100 transition-colors border-r border-gray-100 mr-1"
          title="Erase Background (Auto/Manual)"
        >
          <Eraser className="w-4 h-4 text-purple-600" />
          <span>Erase BG</span>
        </button>

        {/* Delete Button */}
        {onDelete && (
          <div className="flex items-center ml-auto px-2 border-l border-gray-100">
            <button
              type="button"
              onClick={() => selectedImageId && onDelete?.(selectedImageId)}
              className="p-1.5 rounded-md text-red-500 hover:bg-red-50 transition-all"
              title="Delete Image"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="h-px bg-gray-100/60 mx-2" />

      {/* Row 2: Styling */}
      <div className="flex items-center h-9 px-1 gap-0.5">
        {/* Object Fit */}
        <div className="flex items-center gap-0.5 px-1 border-r border-gray-100 mr-1">
          {(['cover', 'contain', 'fill'] as const).map((fit) => (
            <button
              key={fit}
              type="button"
              onClick={() => updateSetting('objectFit', fit, true)}
              className={`h-7 px-2.5 rounded-md text-[11px] font-bold uppercase transition-all ${localSettings.objectFit === fit
                ? 'bg-green-50 text-green-600'
                : 'text-gray-500 hover:bg-gray-100'
                }`}
              title={fit === 'fill' ? 'Stretch to fill' : fit}
            >
              {fit === 'fill' ? 'Stretch' : fit}
            </button>
          ))}
        </div>

        {/* Flip Controls */}
        <div className="flex items-center gap-0.5 px-1 border-r border-gray-100 mr-1">
          <button
            type="button"
            onClick={() => updateSetting('flipHorizontal', !localSettings?.flipHorizontal, true)}
            className={`p-1.5 rounded-md transition-all ${localSettings?.flipHorizontal
              ? 'bg-green-50 text-green-600'
              : 'text-gray-500 hover:bg-gray-100'
              }`}
            title="Flip Horizontal"
          >
            <FlipHorizontal className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => updateSetting('flipVertical', !localSettings?.flipVertical, true)}
            className={`p-1.5 rounded-md transition-all ${localSettings?.flipVertical
              ? 'bg-green-50 text-green-600'
              : 'text-gray-500 hover:bg-gray-100'
              }`}
            title="Flip Vertical"
          >
            <FlipVertical className="w-5 h-5" />
          </button>
        </div>

        {/* Opacity */}
        <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1" title="Opacity">
          <div className="w-5 h-5 text-gray-400">
            <Opacity />
          </div>
          <input
            type="range"
            value={localSettings.opacity ?? 100}
            onChange={(e) => updateSetting('opacity', parseInt(e.target.value), false)}
            onMouseUp={(e) => updateSetting('opacity', parseInt((e.target as HTMLInputElement).value), true)}
            onBlur={(e) => updateSetting('opacity', parseInt((e.target as HTMLInputElement).value), true)}
            min="0"
            max="100"
            className="w-16 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500 hover:accent-green-600"
          />
          <span className="text-[11px] font-bold text-gray-500 w-7 text-right">
            {Math.round(localSettings.opacity)}%
          </span>
        </div>

        {/* Stroke (Border) */}
        <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1" title="Stroke">
          <div className="w-5 h-5 text-gray-400">
            <Border />
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                ref={colorTriggerRef}
                type="button"
                onClick={() => setActivePopover(activePopover === 'border' ? null : 'border')}
                className="flex items-center gap-1.5 bg-gray-50/50 p-1 rounded-full border border-gray-100 hover:bg-gray-100 transition-colors"
              >
                <div
                  className="w-5 h-5 rounded-full border-2 border-white shadow-sm flex items-center justify-center overflow-hidden"
                  style={{
                    backgroundColor: localSettings.borderColor && localSettings.borderColor !== 'transparent' ? localSettings.borderColor : 'transparent',
                    backgroundImage: localSettings.borderColor === 'transparent' ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, #fff 25%, #fff 75%, #ccc 75%, #ccc)' : 'none',
                    backgroundSize: '4px 4px',
                    backgroundPosition: '0 0, 2px 2px'
                  }}
                >
                  {localSettings.borderColor === 'transparent' && <div className="w-full h-0.5 bg-red-500/80 rotate-45" />}
                </div>
                <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${activePopover === 'border' ? 'rotate-180' : ''}`} />
              </button>

              {activePopover === 'border' && createPortal(
                <AnimatePresence>
                  <motion.div
                    id="image-color-portal"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    className="fixed w-[200px] bg-white border border-gray-200 shadow-[0_12px_40px_rgba(0,0,0,0.12)] rounded-xl z-[10000] p-3 overflow-hidden"
                    style={{ top: `${popoverPos.top}px`, left: `${popoverPos.left}px` }}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Border Color</div>
                    <div className="grid grid-cols-5 gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => {
                          updateSetting('borderColor', 'transparent');
                          setActivePopover(null);
                        }}
                        className={`w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center bg-white transition-all hover:scale-110 active:scale-95 ${localSettings.borderColor === 'transparent' ? 'ring-2 ring-[#30A84B] ring-offset-1' : ''}`}
                        title="Transparent"
                      >
                        <div className="w-full h-0.5 bg-red-500/80 rotate-45" />
                      </button>
                      {['#000000', '#ffffff', '#f87171', '#60a5fa', '#34d399', '#fbbf24', '#a78bfa', '#f472b6', '#94a3b8'].map(c => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => {
                            updateSetting('borderColor', c);
                            setActivePopover(null);
                          }}
                          className={`w-7 h-7 rounded-lg border border-gray-100 shadow-sm transition-transform hover:scale-110 active:scale-95 ${localSettings.borderColor === c ? 'ring-2 ring-[#30A84B] ring-offset-1' : ''}`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>

                    <div className="pt-3 border-t border-gray-50 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="relative w-6 h-6 rounded-lg overflow-hidden border border-gray-200 shadow-sm group">
                          <input
                            type="color"
                            value={localSettings.borderColor && localSettings.borderColor !== 'transparent' ? localSettings.borderColor : '#000000'}
                            onChange={(e) => updateSetting('borderColor', e.target.value)}
                            className="absolute inset-[-4px] w-[calc(100%+8px)] h-[calc(100%+8px)] cursor-pointer bg-transparent border-none p-0"
                          />
                        </div>
                        <span className="text-[11px] font-mono font-bold text-gray-500 uppercase">
                          {(localSettings.borderColor && localSettings.borderColor !== 'transparent' ? localSettings.borderColor : '#000000').replace('#', '')}
                        </span>
                      </div>
                      <Palette className="w-3.5 h-3.5 text-gray-400" />
                    </div>
                  </motion.div>
                </AnimatePresence>,
                document.body
              )}
            </div>
            <div className={`flex items-center transition-opacity ${localSettings.borderColor === 'transparent' ? 'opacity-40 grayscale pointer-events-none' : ''}`}>
              <span className="text-[11px] font-mono font-medium text-gray-500 uppercase">
                {(localSettings.borderColor && localSettings.borderColor !== 'transparent' ? localSettings.borderColor : '#000000').replace('#', '')}
              </span>
            </div>
            <PropertyInput
              value={localSettings.borderWidth ?? 0}
              onChange={(val) => updateSetting('borderWidth', val)}
              width="w-7"
              min={0}
            />
          </div>
        </div>

        {/* Corner Radius */}
        <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1" title="Corner Radius">
          <div className="flex items-center group">
            <div className="w-5 h-5 text-gray-400 group-focus-within:text-[#30A84B] transition-colors mr-1.5">
              <Corner />
            </div>
            <PropertyInput
              value={localSettings.borderRadius ?? 0}
              onChange={(val) => updateSetting('borderRadius', val)}
              width="w-7"
              min={0}
            />
          </div>
          <input
            type="range"
            value={localSettings.borderRadius ?? 0}
            onChange={(e) => updateSetting('borderRadius', parseInt(e.target.value))}
            min="0"
            max="100"
            className="w-16 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#30A84B] hover:accent-green-600 transition-all"
          />
        </div>

        {/* Data Binding Selector */}
        <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1" title="Data Binding">
          <div className="relative">
            <button
              type="button"
              onClick={onOpenDataBindingSidepane}
              className={`flex items-center justify-between gap-1.5 h-7 px-2.5 rounded-md hover:bg-gray-100 transition-colors text-[11px] font-bold uppercase tracking-tighter ${
                (localSettings as any).binding ? 'bg-green-50 text-green-600' : 'text-gray-500'
              }`}
              style={{ minWidth: '85px' }}
              title="Configure Custom API Data Binding"
            >
              <div className="flex items-center gap-1.5 truncate">
                <Database className="w-3.5 h-3.5" />
                <span className="truncate max-w-[65px] font-sans font-bold">
                  {(localSettings as any).binding ? (localSettings as any).binding.apiKey.split('.').pop() : 'Static'}
                </span>
              </div>
            </button>
          </div>
        </div>

        {/* Animation Selector */}
        <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1" title="Image Animation">
          <div className="relative">
            <button
              ref={animTriggerRef}
              type="button"
              onClick={() => setIsAnimDropdownOpen(!isAnimDropdownOpen)}
              className="flex items-center gap-1.5 h-7 px-2 rounded-md hover:bg-gray-100 transition-colors group"
            >
              <Sparkles className={`w-4 h-4 transition-colors ${localSettings.animation?.type && localSettings.animation.type !== 'none' ? 'text-[#30A84B]' : 'text-gray-400 group-hover:text-[#30A84B]'}`} />
              <span className="text-[12px] font-bold text-gray-700 min-w-[40px] text-left uppercase tracking-tighter">
                {ANIMATION_OPTIONS.find(a => a?.id === (localSettings.animation?.type || 'none'))?.label || 'Static'}
              </span>
              <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isAnimDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {createPortal(
              <AnimatePresence>
                {isAnimDropdownOpen && (
                  <motion.div
                    id="image-anim-portal"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    className="fixed w-[260px] bg-white border border-gray-200 shadow-[0_12px_40px_rgba(0,0,0,0.12)] rounded-2xl z-[9999] overflow-hidden origin-top-left"
                    style={{ top: `${animDropdownPos?.top}px`, left: `${animDropdownPos?.left}px`, pointerEvents: 'auto' }}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {/* Refined Header */}
                    <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em]">Motion Presets</span>
                      <div className="flex items-center gap-1.5">
                        <div className="w-1 h-1 rounded-full bg-[#30A84B]" />
                        <span className="text-[9px] font-medium text-gray-400">v1.2</span>
                      </div>
                    </div>

                    {/* Enhanced Grid */}
                    <div className="p-2 grid grid-cols-2 gap-2 max-h-[380px] overflow-y-auto scrollbar-none">
                      {ANIMATION_OPTIONS.map((anim) => (
                        <button
                          key={anim.id}
                          type="button"
                          onClick={() => {
                            updateAnimationSetting('type', anim.id);
                            setIsAnimDropdownOpen(false);
                          }}
                          onMouseEnter={() => setHoveredAnim(anim.id)}
                          onMouseLeave={() => setHoveredAnim(null)}
                          className={`group relative flex flex-col items-center justify-center gap-2 p-3.5 rounded-xl border transition-all duration-200 ${localSettings.animation?.type === anim.id
                            ? 'border-[#30A84B] bg-[#30A84B]/[0.03] ring-1 ring-[#30A84B]/20'
                            : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                          {/* Selection Indicator */}
                          {localSettings.animation?.type === anim.id && (
                            <div className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-[#30A84B]" />
                          )}

                          {/* Icon Stage */}
                          <div className={`flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-300 ${localSettings.animation?.type === anim.id
                            ? 'bg-[#30A84B] text-white shadow-[0_4px_12px_rgba(48,168,75,0.25)]'
                            : 'bg-gray-50 text-gray-400 group-hover:bg-white group-hover:text-gray-600 group-hover:shadow-sm'
                            } ${hoveredAnim === anim.id ? anim.previewClass : ''}`}
                            style={{ '--preview-duration': '0.7s', '--preview-iteration': 'infinite' } as any}
                          >
                            <anim.icon className="w-[22px] h-[22px] stroke-[1.8]" />
                          </div>

                          <span className={`text-[11px] font-bold tracking-tight transition-colors ${localSettings.animation?.type === anim.id ? 'text-[#30A84B]' : 'text-gray-500 group-hover:text-gray-900'
                            }`}>
                            {anim.label}
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Clean Subtle Footer */}
                    <div className="p-3 border-t border-gray-50 flex items-center justify-center">
                      <span className="text-[9px] font-medium text-gray-300 italic">Select to apply effect</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>,
              document.body
            )}
          </div>
          {localSettings.animation?.type && localSettings.animation.type !== 'none' && (
            <div className="flex items-center gap-1.5 ml-1">
              <div className="flex items-center gap-1 mr-1">
                <button
                  onClick={() => updateAnimationSetting('duration', 5000)}
                  className="text-[9px] font-bold text-gray-400 hover:text-[#30A84B] px-1 bg-gray-50 rounded h-6"
                  title="Set to 5s"
                >5S</button>
                <button
                  onClick={() => updateAnimationSetting('duration', 10000)}
                  className="text-[9px] font-bold text-gray-400 hover:text-[#30A84B] px-1 bg-gray-50 rounded h-6"
                  title="Set to 10s"
                >10S</button>
              </div>
              <PropertyInput
                value={localSettings.animation.duration || 1000}
                onChange={(val) => updateAnimationSetting('duration', val)}
                width="w-10"
                unit="MS"
                step={100}
                min={0}
              />
              <div className="flex items-center h-7 px-1.5 rounded-lg bg-gray-50 border border-transparent">
                <button
                  onClick={() => updateAnimationSetting('loop', !localSettings.animation.loop)}
                  className={`p-1 rounded-md transition-colors ${localSettings.animation.loop ? 'text-green-600' : 'text-gray-400'}`}
                  title="Repeat Animation"
                >
                  <Repeat className="w-3 h-3" />
                </button>
              </div>
              <select
                value={localSettings.animation.easing || 'ease'}
                onChange={(e) => updateAnimationSetting('easing', e.target.value)}
                className="text-[9px] font-bold text-gray-500 bg-gray-50 border-none outline-none rounded-md px-1 py-0.5 h-7 cursor-pointer hover:bg-gray-100"
              >
                <option value="ease">Ease</option>
                <option value="linear">Lin</option>
                <option value="backOut">Back</option>
                <option value="elasticOut">Elas</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Image Crop Modal (WEJHA-315) */}
      {isCropModalOpen && (
        <ImageCropModal
          isOpen={isCropModalOpen}
          onClose={() => setIsCropModalOpen(false)}
          imageUrl={localSettings.url}
          initialCrop={localSettings.crop}
          onApplyCrop={(cropData, croppedUrl) => {
            console.log('[ImagePropertiesPanel] onApplyCrop received:', {
              cropData,
              hasCroppedUrl: !!croppedUrl,
              croppedUrlPreview: croppedUrl ? croppedUrl.slice(0, 50) + '...' : undefined,
              currentDimensions: dimensions,
            });

            // If canvas produced a cropped URL, the image itself is cropped directly
            const updates: Partial<ImageSettings> = {
              crop: croppedUrl ? { enabled: false, top: 0, right: 0, bottom: 0, left: 0 } : cropData,
            };
            if (croppedUrl) {
              updates.url = croppedUrl;
            }

            // If we have a cropped image URL and valid dimensions, adjust layer bounds on canvas
            if (croppedUrl && dimensions && onDimensionsChange && (cropData.top > 0 || cropData.right > 0 || cropData.bottom > 0 || cropData.left > 0)) {
              const cropWFactor = (100 - cropData.left - cropData.right) / 100;
              const cropHFactor = (100 - cropData.top - cropData.bottom) / 100;
              const newWidth = Math.max(20, Math.round(dimensions.width * cropWFactor));
              const newHeight = Math.max(20, Math.round(dimensions.height * cropHFactor));
              const newLeft = Math.round(dimensions.left + (dimensions.width * (cropData.left / 100)));
              const newTop = Math.round(dimensions.top + (dimensions.height * (cropData.top / 100)));

              console.log('[ImagePropertiesPanel] Recalculated canvas dimensions:', {
                old: dimensions,
                next: { left: newLeft, top: newTop, width: newWidth, height: newHeight },
              });
              onDimensionsChange({ left: newLeft, top: newTop, width: newWidth, height: newHeight });
              onDimensionsCommit?.();
            }

            console.log('[ImagePropertiesPanel] Committing updates atomically:', updates);
            setLocalSettings(prev => ({ ...prev, ...updates }));
            onSettingsChange(updates, true);
          }}
        />
      )}

      {/* Background Eraser Modal (WEJHA-316) */}
      {isEraserModalOpen && (
        <BackgroundEraserModal
          isOpen={isEraserModalOpen}
          onClose={() => setIsEraserModalOpen(false)}
          imageUrl={localSettings.url}
          onApply={(newUrl) => {
            updateSetting('url', newUrl, true);
          }}
        />
      )}
    </div>
  );
};

